import React from 'react';
import {membershipPaymentRecoveryUrl} from '../../../utility/membershipPaymentRecovery';
import {previewHasCoveredMembership} from '../utils';

export function isMembershipV2Pack(pack) {
  return Boolean(pack && pack.membershipV2 === true);
}

const formatCreditCount = value => {
  if (value === 'unlimited') {
    return 'Unlimited';
  }

  if (value === null || value === undefined || value === '') {
    return '0';
  }

  return String(value);
};

const formatDisplayDate = value => {
  if (!value) {
    return null;
  }

  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[2]}/${match[3]}/${match[1]}`;
  }

  return String(value);
};

const previewUsage = preview => preview?.willUse?.[0] ?? null;

const previewDisplaySource = preview =>
  previewUsage(preview) || preview?.coveredBuckets?.[0] || null;

const previewSources = preview => [
  ...(preview?.willUse || []),
  ...(preview?.coveredBuckets || []),
  ...(preview?.eligibleSubscribers || []),
  ...(preview?.coveredSubscribers || []),
];

const previewUsesFamilySharing = preview =>
  previewSources(preview).some(source => source?.familySharingEnabled === true);

const personName = person => {
  if (!person) {
    return null;
  }

  const fullName = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return fullName || person.memberName || person.name || null;
};

export const addMembershipParticipantNames = ({
  participantPreviews = [],
  selectedParticipants = [],
  primaryMember,
}) => {
  const primaryMemberId = primaryMember?.id || primaryMember?.uuid;

  return participantPreviews.map(participant => {
    if (participant?.name) {
      return participant;
    }

    const participantMemberId = String(participant?.participantMemberId || '');
    const selectedParticipant = selectedParticipants.find(candidate => {
      const candidateId =
        candidate?.isParent || candidate?.uuid === '_parent'
          ? primaryMemberId
          : candidate?.uuid || candidate?.id;

      return candidateId && String(candidateId) === participantMemberId;
    });
    const isPrimaryMember = primaryMemberId && String(primaryMemberId) === participantMemberId;
    const matchingPreviewSource = previewSources(participant?.preview).find(
      source => source?.memberId && String(source.memberId) === participantMemberId
    );
    const name =
      personName(selectedParticipant) ||
      (isPrimaryMember ? personName(primaryMember) : null) ||
      personName(matchingPreviewSource);

    return name ? {...participant, name} : participant;
  });
};

const packageLabelForPreview = preview => {
  const usage = previewDisplaySource(preview);
  if (!usage) {
    return null;
  }

  if (usage.packageNumber) {
    return `Package ${usage.packageNumber}`;
  }

  if (usage.label) {
    const planPrefix = usage.planName ? `${usage.planName} - ` : '';
    return usage.label.startsWith(planPrefix)
      ? usage.label.substring(planPrefix.length)
      : usage.label;
  }

  return null;
};

const mainMessage = (preview, pack) => {
  if (preview?.messages?.length > 0) {
    return preview.messages[0];
  }

  if (pack?.borrowed) {
    return 'This booking would borrow 1 future membership credit.';
  }

  if (isMembershipV2Pack(pack)) {
    return 'This booking would use 1 membership credit.';
  }

  return null;
};

const aggregateCreditUsageMessage = preview => {
  const usages = preview?.willUse || [];
  const quantity = usages.reduce((total, usage) => {
    const usageQuantity = Number(usage?.quantity ?? 1);
    return total + (usageQuantity > 0 ? usageQuantity : 1);
  }, 0);

  if (quantity <= 1) {
    return null;
  }

  const borrowed = usages.every(usage => usage?.borrowed === true);
  const expiryDates = [...new Set(usages.map(usage => usage?.expiresAt).filter(Boolean))];
  const expiryText =
    expiryDates.length === 1 ? ` expiring ${formatDisplayDate(expiryDates[0])}` : '';

  return borrowed
    ? `This booking would borrow ${quantity} future membership credits${expiryText}.`
    : `This booking would use ${quantity} membership credits${expiryText}.`;
};

const planName = (preview, pack) => {
  const sourceCount =
    preview?.eligibleSubscribers?.length ||
    preview?.coveredSubscribers?.length ||
    pack?.eligibleMembershipCount ||
    0;

  if (sourceCount > 1) {
    return preview?.eligible === false && preview?.coveredSubscribers?.length > 0
      ? `${sourceCount} memberships`
      : `${sourceCount} eligible memberships`;
  }

  return (
    previewDisplaySource(preview)?.planName ||
    preview?.coveredSubscribers?.[0]?.planName ||
    pack?.name ||
    'Membership'
  );
};

const nextCreditLabel = (preview, pack) => {
  const usage = previewUsage(preview);
  const plan = usage?.planName || pack?.nextCreditPlanName;
  if (!plan) {
    return null;
  }

  const packageNumber = usage?.packageNumber || pack?.nextCreditPackageNumber;
  return packageNumber ? `${plan} - Package ${packageNumber}` : plan;
};

const membershipSources = (preview, pack) => {
  if (preview?.eligibleSubscribers?.length > 0) {
    return preview.eligibleSubscribers;
  }

  if (preview?.coveredSubscribers?.length > 0) {
    return preview.coveredSubscribers;
  }

  return pack?.eligibleMemberships || [];
};

const sourceCreditText = source => {
  if (source?.hasUnlimited) {
    return 'Unlimited';
  }

  const current = source?.currentBillingCycleCredits ?? source?.currentCredits ?? 0;
  const rollover = source?.rolloverCreditsAvailable ?? 0;
  const future = Number(source?.borrowableFutureCredits || 0);
  const parts = [`Current ${formatCreditCount(current)}`];

  if (source?.allowsCarryForward) {
    parts.push(`Rollover ${formatCreditCount(rollover)}`);
  }

  if (future > 0) {
    parts.push(`${future} future`);
  }

  return parts.join(', ');
};

const currentBillingCycleCredits = (preview, pack) =>
  preview?.currentBillingCycleCredits ??
  pack?.currentBillingCycleCredits ??
  (pack?.borrowed ? 0 : pack?.remaining ?? 0);

const rolloverCreditsAvailable = (preview, pack) =>
  preview?.rolloverCreditsAvailable ?? pack?.rolloverCreditsAvailable ?? 0;

const packageCreditsPerBillingCycle = (preview, pack) =>
  preview?.packageCreditsPerBillingCycle ??
  previewDisplaySource(preview)?.packageCreditsPerBillingCycle ??
  pack?.packageCreditsPerBillingCycle ??
  0;

const allowsCarryForward = (preview, pack) =>
  Boolean(
    preview?.allowsCarryForward ||
      pack?.allowsCarryForward ||
      pack?.rollover === true ||
      pack?.creditPolicy === 'carry_forward'
  );

const usageExpiryText = preview => {
  const usage = previewUsage(preview);
  if (!usage?.expiresAt) {
    return null;
  }

  return `Credit expires on ${formatDisplayDate(usage.expiresAt)}`;
};

const usageActivationText = preview => {
  const usage = previewUsage(preview);
  if (!usage?.borrowed || !usage?.activeFrom) {
    return null;
  }

  return `Borrowed credit becomes active on ${formatDisplayDate(usage.activeFrom)}`;
};

const MembershipPausedLine = ({preview}) => (
  <div className="cf-booking-credit-preview__line">
    <strong>Membership Status:</strong> Paused effective as of{' '}
    {formatDisplayDate(preview?.pauseEffectiveDate) || 'the selected date'}
  </div>
);

const FutureBillingCycleLines = ({preview, pack}) => {
  const startDate = formatDisplayDate(
    preview?.currentBillingCycleStartDate ?? previewDisplaySource(preview)?.cycleStartDate
  );
  const endDate = formatDisplayDate(
    preview?.currentBillingCycleEndDate ?? previewDisplaySource(preview)?.cycleEndDate
  );

  return (
    <>
      <div className="cf-booking-credit-preview__line">
        <strong>Current billing cycle:</strong>{' '}
        {startDate && endDate ? `${startDate} through ${endDate}` : 'Not available'}
      </div>
      {allowsCarryForward(preview, pack) && (
        <div className="cf-booking-credit-preview__line">
          <strong>Rollover Credits Available:</strong>{' '}
          {formatCreditCount(rolloverCreditsAvailable(preview, pack))}
        </div>
      )}
    </>
  );
};

const CreditAvailabilityLines = ({preview, pack}) => {
  const showRollover = allowsCarryForward(preview, pack);

  return (
    <>
      <div className="cf-booking-credit-preview__line">
        <strong>Package Credits Per Billing Cycle:</strong>{' '}
        {formatCreditCount(packageCreditsPerBillingCycle(preview, pack))}
      </div>
      <div className="cf-booking-credit-preview__line">
        <strong>Remaining Credits for this Billing Cycle:</strong>{' '}
        {formatCreditCount(currentBillingCycleCredits(preview, pack))}
      </div>
      {showRollover && (
        <div className="cf-booking-credit-preview__line">
          <strong>Rollover Credits Available:</strong>{' '}
          {formatCreditCount(rolloverCreditsAvailable(preview, pack))}
        </div>
      )}
    </>
  );
};

const participantStatus = preview => {
  if (!preview) {
    return 'No preview available';
  }

  if (preview.eligible !== true) {
    if (preview.membershipPaymentRequired === true) {
      return 'Missed payment';
    }

    if (preview.membershipPaused === true) {
      return 'Membership paused';
    }

    if (preview.futureBillingCycle === true || preview.futureBillingCycleNoRollover === true) {
      return 'Past current billing cycle';
    }

    return preview.hasCoveredMembership === true
      ? 'No membership credits available'
      : 'No eligible membership credit';
  }

  if (preview.unlimited === true) {
    return 'Unlimited membership credit';
  }

  if ((preview.borrowableFuture ?? 0) > 0 && (preview.availableNow ?? 0) === 0) {
    return 'Borrowed future membership credit';
  }

  return 'Membership credit available';
};

const BookingCreditPreviewCard = ({
  preview,
  pack,
  participantPreviews = [],
  title = 'Membership Credits',
  className = '',
}) => {
  const showCard =
    previewHasCoveredMembership(preview) ||
    participantPreviews.some(participant => previewHasCoveredMembership(participant?.preview));

  if (!showCard) {
    return null;
  }

  const aggregatePreview = preview || {};
  const participantRows = participantPreviews.filter(participant => participant?.preview);
  const paymentRequiredPreview = [
    aggregatePreview,
    ...participantRows.map(row => row.preview),
  ].find(candidate => candidate?.membershipPaymentRequired === true);
  const membershipPaymentRequired = Boolean(paymentRequiredPreview);
  const familySharingPlan =
    participantRows.length > 0
      ? participantRows.every(participant => previewUsesFamilySharing(participant.preview))
      : previewUsesFamilySharing(aggregatePreview);
  const displayPreview = familySharingPlan
    ? participantRows[0]?.preview || aggregatePreview
    : aggregatePreview;
  const plan = planName(displayPreview, pack);
  const paymentAttention =
    paymentRequiredPreview?.paymentAttention ||
    paymentRequiredPreview?.missedPaymentSubscribers?.[0] ||
    null;
  const paymentRecoveryUrl = membershipPaymentRecoveryUrl(paymentAttention);
  const packageLabel = packageLabelForPreview(displayPreview);
  const sourceList = membershipSources(displayPreview, pack);
  const nextCredit = nextCreditLabel(displayPreview, pack);
  const requiresPackageCredits = aggregatePreview.requiresPackageCredits === true;
  const membershipCreditsToUse = Number(aggregatePreview.membershipCreditsToUse || 0);
  const selectedParticipantCount = Number(aggregatePreview.selectedParticipantCount || 0);
  const uncoveredParticipantCount = Number(aggregatePreview.uncoveredParticipantCount || 0);
  const message =
    (requiresPackageCredits && aggregatePreview?.messages?.[0]) ||
    (familySharingPlan && aggregateCreditUsageMessage(aggregatePreview)) ||
    mainMessage(displayPreview, pack);
  const hideUnlimitedUsageMessage =
    displayPreview.unlimited === true || pack?.isQuantityUnlimited === true;
  const expiryText = usageExpiryText(displayPreview);
  const activationText = usageActivationText(displayPreview);
  const membershipPaused = displayPreview.membershipPaused === true;
  const futureBillingCycle =
    displayPreview.futureBillingCycle === true ||
    displayPreview.futureBillingCycleNoRollover === true;
  const creditsExhausted =
    displayPreview.eligible !== true &&
    displayPreview.hasCoveredMembership === true &&
    !membershipPaused &&
    !futureBillingCycle;
  const sourceListTitle = displayPreview.eligible === true ? 'Eligible Memberships' : 'Memberships';
  const statusClass = displayPreview.eligible === true ? 'badge-success' : 'badge-secondary';
  const messageClassName =
    !requiresPackageCredits &&
    (displayPreview.eligible === true || membershipPaused || futureBillingCycle || creditsExhausted)
      ? 'alert-info'
      : 'alert-warning';
  const description = requiresPackageCredits
    ? `Membership credits cover ${membershipCreditsToUse} of ${selectedParticipantCount} selected participants.`
    : membershipPaused
    ? 'Membership credits are unavailable while this membership is paused.'
    : futureBillingCycle
    ? 'The selected booking date is past the current billing cycle.'
    : creditsExhausted
    ? 'No membership credits remain for this billing cycle.'
    : 'This booking will use membership credits if an eligible plan is available.';
  const showAggregateSummary = participantRows.length === 0 || familySharingPlan;
  const showParticipantRows = participantRows.length > 0 && !familySharingPlan;

  if (membershipPaymentRequired) {
    return (
      <div className={`cf-booking-credit-preview border rounded bg-light p-3 ${className}`.trim()}>
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <div className="font-weight-bold text-dark">Membership Info</div>
            <div className="small text-muted">
              This covered service cannot be booked until the missed membership payment is resolved.
            </div>
          </div>
          <span className="badge badge-pill badge-danger mt-2 mt-sm-0">Missed payment</span>
        </div>

        <div className="mt-3 cf-booking-credit-preview__line">
          <strong>Plan:</strong> {paymentAttention?.planName || plan}
        </div>

        <div className="alert alert-warning mb-0 mt-3 py-2">
          <div>
            Your membership has a missed payment. Update your saved card and resolve the payment
            before booking this service.
          </div>
          {paymentRecoveryUrl && (
            <a className="btn btn-primary btn-sm mt-2" href={paymentRecoveryUrl}>
              Go to Saved Card and Card Update
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`cf-booking-credit-preview border rounded bg-light p-3 ${className}`.trim()}>
      {showAggregateSummary ? (
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <div className="font-weight-bold text-dark">{title}</div>
            <div className="small text-muted">{description}</div>
          </div>
          <span
            className={`badge badge-pill ${
              requiresPackageCredits ? 'badge-warning' : statusClass
            } mt-2 mt-sm-0`}
          >
            {requiresPackageCredits
              ? `Package needed for ${uncoveredParticipantCount} ${
                  uncoveredParticipantCount === 1 ? 'participant' : 'participants'
                }`
              : participantStatus(displayPreview)}
          </span>
        </div>
      ) : (
        <div className="font-weight-bold text-dark">{title}</div>
      )}

      {showAggregateSummary && (
        <>
          <div className="mt-3">
            <div className="cf-booking-credit-preview__line">
              <strong>
                {sourceList.length > 1 && !membershipPaused && !futureBillingCycle
                  ? 'Credit Sources:'
                  : 'Plan:'}
              </strong>{' '}
              {plan}
            </div>
            {nextCredit && sourceList.length > 1 && !membershipPaused && !futureBillingCycle && (
              <div className="cf-booking-credit-preview__line">
                <strong>Next Credit From:</strong> {nextCredit}
              </div>
            )}
            {packageLabel && sourceList.length <= 1 && !membershipPaused && (
              <div className="cf-booking-credit-preview__line">
                <strong>Package:</strong> {packageLabel}
              </div>
            )}
            {membershipPaused ? (
              <MembershipPausedLine preview={displayPreview} />
            ) : futureBillingCycle ? (
              <FutureBillingCycleLines preview={displayPreview} pack={pack} />
            ) : (
              <CreditAvailabilityLines preview={displayPreview} pack={pack} />
            )}
          </div>

          {sourceList.length > 1 && !membershipPaused && !futureBillingCycle && (
            <div className="mt-3">
              <div className="font-weight-bold text-dark mb-2">{sourceListTitle}</div>
              <div className="list-group">
                {sourceList.map(source => (
                  <div
                    key={source.subscriberId || source.planId || source.planName}
                    className="list-group-item py-2 d-flex justify-content-between align-items-start"
                  >
                    <span>{source.planName || 'Membership'}</span>
                    <span className="badge badge-light border text-dark">
                      {sourceCreditText(source)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {message && !hideUnlimitedUsageMessage && (
            <div className={`alert ${messageClassName} mb-0 mt-3 py-2`}>{message}</div>
          )}

          {(expiryText || activationText) && (
            <div className="small text-muted mt-2">
              {expiryText && <div>{expiryText}</div>}
              {activationText && <div>{activationText}</div>}
            </div>
          )}
        </>
      )}

      {showParticipantRows && (
        <div className="mt-3">
          <div className="font-weight-bold text-dark mb-2">Selected Participants</div>
          <div className="list-group">
            {participantRows.map(participant => {
              const participantPreview = participant.preview;
              const eligible = participantPreview?.eligible === true;
              const participantPaused = participantPreview?.membershipPaused === true;
              const participantFutureBillingCycle =
                participantPreview?.futureBillingCycle === true ||
                participantPreview?.futureBillingCycleNoRollover === true;
              const participantPackage = packageLabelForPreview(participantPreview);
              const participantMessage =
                participantPreview?.messages?.[0] ||
                (eligible
                  ? 'This participant can use a membership credit for this booking.'
                  : participantPreview?.hasCoveredMembership === true
                  ? 'No membership credits are available for this participant on the selected booking date.'
                  : 'No eligible membership credit is available for this participant on the selected booking date.');
              const participantMessageClassName =
                participantPaused ||
                participantFutureBillingCycle ||
                (participantPreview?.hasCoveredMembership === true && !eligible)
                  ? 'alert alert-info mb-0 mt-2 py-2'
                  : 'small text-muted mt-2';

              return (
                <div
                  key={participant.participantMemberId || participant.name}
                  className="list-group-item"
                >
                  <div className="d-flex justify-content-between align-items-start flex-wrap">
                    <strong className="text-dark">{participant.name || 'Participant'}</strong>
                    <span
                      className={`badge badge-pill ${
                        eligible ? 'badge-success' : 'badge-secondary'
                      } mt-2 mt-sm-0`}
                    >
                      {participantStatus(participantPreview)}
                    </span>
                  </div>
                  {(eligible || participantPreview?.hasCoveredMembership === true) && (
                    <div className="small mt-2">
                      <div>
                        <strong>Plan:</strong> {planName(participantPreview, null)}
                      </div>
                      {participantPackage && !participantPaused && (
                        <div>
                          <strong>Package:</strong> {participantPackage}
                        </div>
                      )}
                      {participantPaused ? (
                        <MembershipPausedLine preview={participantPreview} />
                      ) : participantFutureBillingCycle ? (
                        <FutureBillingCycleLines preview={participantPreview} pack={null} />
                      ) : (
                        <CreditAvailabilityLines preview={participantPreview} pack={null} />
                      )}
                    </div>
                  )}
                  <div className={participantMessageClassName}>{participantMessage}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingCreditPreviewCard;
